import dayjs from "dayjs"
import "dayjs/locale/sv"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { z } from "zod"
import { Time } from "../common"
import { WeekDay } from "../common/Weekday"
import { TranslationKey, Translator } from "../i18n/translation-key"

dayjs.extend(utc)
dayjs.extend(timezone)

const STOCKHOLM_TZ = "Europe/Stockholm"

export interface TimePeriod {
  opensAt: Time
  closesAt: Time
}

export interface OpeningHours {
  isClosedAllDay: boolean
  periods: TimePeriod[]
}

export interface WeekDayOpeningHours extends OpeningHours {
  weekDay: WeekDay
}

export interface SpecialOpeningHours extends OpeningHours {
  date: Date
}

export interface AllOpeningHours {
  weekly: WeekDayOpeningHours[]
  special?: SpecialOpeningHours[] | null
}

const TimeSchema = z.object({
  hours: z.number().int().min(0).max(23),
  minutes: z.number().int().min(0).max(59),
})

const TimePeriodSchema = z.object({
  opensAt: TimeSchema,
  closesAt: TimeSchema,
})

const OpeningHoursSchema = z.object({
  isClosedAllDay: z.boolean(),
  periods: z.array(TimePeriodSchema),
})

const AllOpeningHoursSchema = z.object({
  weekly: z.array(
    OpeningHoursSchema.extend({ weekDay: z.nativeEnum(WeekDay) }),
  ),
  special: z
    .array(OpeningHoursSchema.extend({ date: z.coerce.date<Date>() }))
    .nullable()
    .optional(),
})

export const SetOpeningHoursSchema = z.object({
  openingHours: AllOpeningHoursSchema.nullable(),
})

export type SetOpeningHoursBody = z.infer<typeof SetOpeningHoursSchema>

/**
 * Legacy format stored in DB before multi-period support.
 * Had opensAt/closesAt directly on the opening hours object instead of periods[].
 */
interface LegacyOpeningHoursDay {
  isClosedAllDay: boolean
  periods?: TimePeriod[]
  opensAt?: Time
  closesAt?: Time
}

interface RawAllOpeningHours {
  weekly: (LegacyOpeningHoursDay & { weekDay: WeekDay })[]
  special?: (LegacyOpeningHoursDay & { date: Date })[] | null
}

/**
 * Normalizes opening hours from old DB format (single opensAt/closesAt)
 * to new format (periods array). Handles the transition period where
 * stored JSON may still have the old shape.
 */
export function normalizeOpeningHours(
  raw: RawAllOpeningHours,
): AllOpeningHours {
  return {
    weekly: raw.weekly.map((day) => ({
      weekDay: day.weekDay,
      isClosedAllDay: day.isClosedAllDay,
      periods: normalizePeriodsFromLegacy(day),
    })),
    special:
      raw.special?.map((day) => ({
        date: day.date,
        isClosedAllDay: day.isClosedAllDay,
        periods: normalizePeriodsFromLegacy(day),
      })) ?? null,
  }
}

const JS_DAY_TO_WEEKDAY: WeekDay[] = [
  WeekDay.SUNDAY,
  WeekDay.MONDAY,
  WeekDay.TUESDAY,
  WeekDay.WEDNESDAY,
  WeekDay.THURSDAY,
  WeekDay.FRIDAY,
  WeekDay.SATURDAY,
]

export function isStoreOpenNow(
  openingHours: AllOpeningHours,
  now: Date = new Date(),
): boolean {
  const stockholmNow = dayjs(now).tz(STOCKHOLM_TZ)
  const dayHours = findDayOpeningHours(
    openingHours.weekly,
    openingHours.special,
    now,
  )

  if (!dayHours || dayHours.isClosedAllDay || dayHours.periods.length === 0) {
    return false
  }

  const currentMinutes = stockholmNow.hour() * 60 + stockholmNow.minute()

  return dayHours.periods.some((period) => {
    const opensAtMinutes = period.opensAt.hours * 60 + period.opensAt.minutes
    const closesAtMinutes = period.closesAt.hours * 60 + period.closesAt.minutes
    return currentMinutes >= opensAtMinutes && currentMinutes < closesAtMinutes
  })
}

/**
 * Lenient view of the stored opening-hours JSON that reads nothing but the
 * closed-all-day flags. Tolerates the legacy shape that
 * `normalizeOpeningHours` migrates, since those flags exist in both shapes.
 */
export const OpeningHoursClosedFlagsSchema = z.object({
  weekly: z.array(
    z.object({
      weekDay: z.nativeEnum(WeekDay),
      isClosedAllDay: z.boolean(),
    }),
  ),
  special: z
    .array(
      z.object({ date: z.coerce.date<Date>(), isClosedAllDay: z.boolean() }),
    )
    .nullish(),
})

export type OpeningHoursClosedFlags = z.infer<
  typeof OpeningHoursClosedFlagsSchema
>

const OpeningTimesSchema = z.object({
  periods: z.array(z.object({ opensAt: TimeSchema })).optional(),
  // The legacy shape, see normalizeOpeningHours.
  opensAt: TimeSchema.nullish(),
})

/**
 * The closed-all-day flags plus when the store opens. Reads nothing about when
 * it closes, so odd closing times can't lose the flags.
 */
export const OpeningHoursWithOpeningTimesSchema = z.object({
  weekly: z.array(
    OpeningHoursClosedFlagsSchema.shape.weekly.element.merge(
      OpeningTimesSchema,
    ),
  ),
  special: z
    .array(
      z
        .object({ date: z.coerce.date<Date>(), isClosedAllDay: z.boolean() })
        .merge(OpeningTimesSchema),
    )
    .nullish(),
})

export type OpeningHoursWithOpeningTimes = z.infer<
  typeof OpeningHoursWithOpeningTimesSchema
>

/**
 * Resolves the entry that applies to a given calendar day in Stockholm.
 * A `special` entry for that date fully masks the weekly entry — including
 * when the special entry opens a weekday that is normally closed.
 */
function findDayOpeningHours<
  TWeekly extends { weekDay: WeekDay; isClosedAllDay: boolean },
  TSpecial extends { date: Date; isClosedAllDay: boolean },
>(
  weekly: TWeekly[],
  special: TSpecial[] | null | undefined,
  date: Date,
): TWeekly | TSpecial | undefined {
  const stockholmDate = dayjs(date).tz(STOCKHOLM_TZ)
  const dateStr = stockholmDate.format("YYYY-MM-DD")

  const specialDay = special?.find(
    (s) => dayjs(s.date).tz(STOCKHOLM_TZ).format("YYYY-MM-DD") === dateStr,
  )

  if (specialDay) return specialDay

  return weekly.find(
    (w) => w.weekDay === JS_DAY_TO_WEEKDAY[stockholmDate.day()],
  )
}

/**
 * Whether the store is closed for the whole of the given calendar day.
 *
 * This is the rule every bookability check applies before it looks at
 * capacity: a closed store cannot be booked, no matter how many bookable
 * slots are configured for that date.
 *
 * Stores with no opening hours on file are not treated as closed.
 */
export function isStoreClosedAllDayOn(
  openingHours: OpeningHoursClosedFlags | null | undefined,
  date: Date,
): boolean {
  if (!openingHours) return false

  return (
    findDayOpeningHours(openingHours.weekly, openingHours.special, date)
      ?.isClosedAllDay ?? false
  )
}

/**
 * When the store first opens on the given calendar day, or null if it's closed
 * or no opening time is on file. A store closed over lunch opens with its
 * earliest period.
 */
export function getFirstOpeningTimeOn(
  openingHours: OpeningHoursWithOpeningTimes | null | undefined,
  date: Date,
): Time | null {
  if (!openingHours) return null

  const dayHours = findDayOpeningHours(
    openingHours.weekly,
    openingHours.special,
    date,
  )
  if (!dayHours || dayHours.isClosedAllDay) return null

  const openingTimes = dayHours.periods?.length
    ? dayHours.periods.map((period) => period.opensAt)
    : dayHours.opensAt
      ? [dayHours.opensAt]
      : []

  return openingTimes.reduce<Time | null>(
    (earliest, time) =>
      !earliest ||
      time.hours * 60 + time.minutes < earliest.hours * 60 + earliest.minutes
        ? time
        : earliest,
    null,
  )
}

const WEEKDAY_ORDER: WeekDay[] = [
  WeekDay.MONDAY,
  WeekDay.TUESDAY,
  WeekDay.WEDNESDAY,
  WeekDay.THURSDAY,
  WeekDay.FRIDAY,
  WeekDay.SATURDAY,
  WeekDay.SUNDAY,
]

const WEEKDAY_SHORT_NAME_KEYS: Record<WeekDay, TranslationKey> = {
  [WeekDay.MONDAY]: "common.weekdayShort.monday",
  [WeekDay.TUESDAY]: "common.weekdayShort.tuesday",
  [WeekDay.WEDNESDAY]: "common.weekdayShort.wednesday",
  [WeekDay.THURSDAY]: "common.weekdayShort.thursday",
  [WeekDay.FRIDAY]: "common.weekdayShort.friday",
  [WeekDay.SATURDAY]: "common.weekdayShort.saturday",
  [WeekDay.SUNDAY]: "common.weekdayShort.sunday",
}

function formatTime(time: Time): string {
  const hours = String(time.hours).padStart(2, "0")
  const minutes = String(time.minutes).padStart(2, "0")
  return `${hours}:${minutes}`
}

function formatPeriodsForDay(periods: TimePeriod[], t: Translator): string {
  if (periods.length === 0) return t("common.openingHours.closed")
  return periods
    .map((p) => `${formatTime(p.opensAt)}-${formatTime(p.closesAt)}`)
    .join(", ")
}

function periodsAreEqual(a: TimePeriod[], b: TimePeriod[]): boolean {
  if (a.length !== b.length) return false
  return a.every(
    (period, i) =>
      period.opensAt.hours === b[i]!.opensAt.hours &&
      period.opensAt.minutes === b[i]!.opensAt.minutes &&
      period.closesAt.hours === b[i]!.closesAt.hours &&
      period.closesAt.minutes === b[i]!.closesAt.minutes,
  )
}

function dayHoursAreEqual(a: OpeningHours, b: OpeningHours): boolean {
  if (a.isClosedAllDay && b.isClosedAllDay) return true
  if (a.isClosedAllDay !== b.isClosedAllDay) return false
  return periodsAreEqual(a.periods, b.periods)
}

/**
 * Formats opening hours into a human-readable multi-line string.
 * Groups consecutive days with identical hours. Appends a section
 * for upcoming "Avvikande öppettider" (special days) when present.
 *
 * Example output:
 * "Mån-Fre: 08:00-18:00
 *  Lör: 10:00-15:00
 *  Sön: Stängt
 *
 *  Avvikande öppettider:
 *  2 maj: 10:00-14:00
 *  6 maj: Stängt"
 */
export function formatOpeningHours(
  openingHours: AllOpeningHours,
  t: Translator,
  now: Date = new Date(),
): string {
  const sortedDays = WEEKDAY_ORDER.map((weekDay) => {
    const dayHours = openingHours.weekly.find((w) => w.weekDay === weekDay)
    return { weekDay, dayHours }
  }).filter(
    (entry): entry is { weekDay: WeekDay; dayHours: WeekDayOpeningHours } =>
      entry.dayHours !== undefined,
  )

  const groups: Array<{ days: WeekDay[]; hours: OpeningHours }> = []

  for (const { weekDay, dayHours } of sortedDays) {
    const lastGroup = groups[groups.length - 1]
    if (lastGroup && dayHoursAreEqual(lastGroup.hours, dayHours)) {
      lastGroup.days.push(weekDay)
    } else {
      groups.push({ days: [weekDay], hours: dayHours })
    }
  }

  const weeklyText = groups
    .map((group) => {
      const firstDay = t(WEEKDAY_SHORT_NAME_KEYS[group.days[0]!])
      const lastDay = t(
        WEEKDAY_SHORT_NAME_KEYS[group.days[group.days.length - 1]!],
      )
      const dayLabel =
        group.days.length === 1 ? firstDay : `${firstDay}-${lastDay}`

      const hoursLabel = group.hours.isClosedAllDay
        ? t("common.openingHours.closed")
        : formatPeriodsForDay(group.hours.periods, t)

      return `${dayLabel}: ${hoursLabel}`
    })
    .join("\n")

  const specialText = formatSpecialOpeningHours(openingHours.special, t, now)

  if (!weeklyText) return specialText
  if (!specialText) return weeklyText
  return `${weeklyText}\n\n${specialText}`
}

function formatSpecialOpeningHours(
  special: SpecialOpeningHours[] | null | undefined,
  t: Translator,
  now: Date,
): string {
  if (!special || special.length === 0) return ""

  const today = new Date(now)
  today.setHours(0, 0, 0, 0)

  const upcoming = special
    .filter((s) => {
      const d = new Date(s.date)
      d.setHours(0, 0, 0, 0)
      return d >= today
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  if (upcoming.length === 0) return ""

  const lines = upcoming.map((s) => {
    const dateLabel = dayjs(s.date).locale("sv").format("D MMMM")
    const hoursLabel = s.isClosedAllDay
      ? t("common.openingHours.closed")
      : formatPeriodsForDay(s.periods, t)
    return `${dateLabel}: ${hoursLabel}`
  })

  return [t("common.openingHours.specialHoursHeading"), ...lines].join("\n")
}

/**
 * Returns today's closing time in "HH:MM" format, considering special hours.
 * Returns null if the store is closed today.
 */
export function getTodaysClosingTime(
  openingHours: AllOpeningHours,
  now: Date = new Date(),
): string | null {
  const dayHours = findDayOpeningHours(
    openingHours.weekly,
    openingHours.special,
    now,
  )

  if (!dayHours || dayHours.isClosedAllDay || dayHours.periods.length === 0) {
    return null
  }

  // Find the last closing time of the day
  const lastPeriod = dayHours.periods.reduce((latest, period) => {
    const latestMinutes = latest.closesAt.hours * 60 + latest.closesAt.minutes
    const periodMinutes = period.closesAt.hours * 60 + period.closesAt.minutes
    return periodMinutes > latestMinutes ? period : latest
  })

  return formatTime(lastPeriod.closesAt)
}

function normalizePeriodsFromLegacy(day: LegacyOpeningHoursDay): TimePeriod[] {
  if (Array.isArray(day.periods)) {
    return day.periods
  }

  if (day.isClosedAllDay || !day.opensAt || !day.closesAt) {
    return []
  }

  return [{ opensAt: day.opensAt, closesAt: day.closesAt }]
}
